import { PLATFORM, SupportedPlatformsEnum } from 'src/dataSource/types'
import type { Platforms } from 'shared/api/accounts'
import {
  ErrorCodesEnum,
  prepareThirdPartyDataSource,
  type PrepareThirdPartyResponse,
  reconnectThirdPartyDataSource,
  SUPERMETRICS_ERROR_CODES,
  THIRD_PARTY_PROVIDER,
  type ThirdPartyPlatforms,
  type ThirdPartyProviders,
  validateThirdPartyId
} from 'shared/thirdParty'
import { ASSETS_BASE_URL } from 'shared/constants'
import { generateOauthLoginUrl } from 'src/dataSource/oauthUrls'
import { capitalize } from 'shared/utils/stringFormatter'
import { z as zod } from 'zod'
import { INTERCOM_ARTICLE_IDS, type IntercomArticleId } from 'shared/constants/documentation'
import { type SupportedPlatform as FivetranSupportedPlatform } from 'shared/thirdParty/fivetran/platforms'
import { HttpStatusCodes } from 'shared/httpStatus'

export { CREDENTIAL_ID_KEY, ERROR_KEY } from 'src/dataSource/constants/queryKeys'

const USE_PREPARING_PAGE_FOR_FIVETRAN = true
const PLATFORM_UNKNOWN = 'unknown'

export interface IDataSourcePlatform {
  label: string
  platform: Platforms | ThirdPartyProviders
  iconUrl: string
  commonIconUrl: string
  oauthUrl: string
  supportsServiceAccountAccess: boolean
  externalEntityLabel: string
  externalEntitiesLabel: string
  isThirdPartyProvider: boolean
  isDataSource?: false
  isConnectable?: false
  isUnknown?: boolean
  supportsReconnect?: boolean
  isInternalOnly: boolean
}

export interface IPlatform extends IDataSourcePlatform {
  platform: Platforms
  thirdPartyProvider?: ThirdPartyProviders
  isSupportedByChannelGrouping: boolean
  uiUrlTemplate?: string
  isManualAccountInput: boolean
  isApiKeyInput?: boolean
  isThirdPartyProvider: false
  disabledMessage?: string
  requiresSingleConnector?: boolean
  intercomArticleId?: IntercomArticleId
  category: PlatformCategory
  isDeprecated?: boolean
}

export interface IThirdPartyPlatform extends IPlatform {
  platform: ThirdPartyPlatforms
  thirdPartyProvider: ThirdPartyProviders
}

export interface IThirdPartyProvider extends IDataSourcePlatform {
  platform: ThirdPartyProviders
  logoUrl: string
  isThirdPartyProvider: true
  multipleTablesPerConnector?: true
  filterCredentialsByClient?: boolean
  prepare: (
    clientId: number,
    platform: ThirdPartyPlatforms,
    credentialId: string | null
  ) => Promise<PrepareThirdPartyResponse>
  reconnect: (
    clientId: number,
    platform: ThirdPartyPlatforms,
    credentialId: string,
    thirdPartyId: string,
    reconnectAccountId?: string | null
  ) => Promise<PrepareThirdPartyResponse>
  hasPlatformSpecificCredentials: boolean
  shouldReconnect: (
    credentialId?: string | null,
    thirdPartyId?: string | null,
    reconnectAccountId?: string | null
  ) => boolean
}

export const PLATFORM_CATEGORIES: Record<string, PlatformCategory> = {
  webAnalytics: 'Web Analytics',
  paidMedia: 'Paid Media',
  dataTargets: 'Data Targets',
  seo: 'SEO',
  other: 'Other',
  thirdPartyConnectors: 'Third-Party Connectors',
  crmMarketingAutomation: 'CRM / Marketing Automation',
  affiliateMarketing: 'Affiliate Marketing',
  emailMarketing: 'Email Marketing'
}

export type PlatformCategory =
  | 'Web Analytics'
  | 'Paid Media'
  | 'Data Targets'
  | 'SEO'
  | 'Other'
  | 'Third-Party Connectors'
  | 'CRM / Marketing Automation'
  | 'Affiliate Marketing'
  | 'Email Marketing'

export const THIRD_PARTY_PLATFORMS: IThirdPartyPlatform[] = [
  {
    label: 'Adobe Analytics',
    platform: PLATFORM.ADOBE_ANALYTICS,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/adobe_analytics.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/adobe_analytics.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.ADOBE_ANALYTICS,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsReconnect: false,
    category: PLATFORM_CATEGORIES.webAnalytics,
    isInternalOnly: false
  },
  {
    label: 'Pinterest Ads',
    platform: PLATFORM.PINTEREST_ADS,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.FIVETRAN,
    iconUrl: `${ASSETS_BASE_URL}/pinterest.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/pinterest.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.FIVETRAN,
      PLATFORM.PINTEREST_ADS,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    requiresSingleConnector: true,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: true
  },
  {
    label: 'Adtraction',
    platform: PLATFORM.ADTRACTION,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/adtraction.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/adtraction.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.ADTRACTION,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    isThirdPartyProvider: false,
    isManualAccountInput: false,
    supportsServiceAccountAccess: false,
    supportsReconnect: false,
    category: PLATFORM_CATEGORIES.affiliateMarketing,
    isInternalOnly: false
  },
  {
    label: 'HubSpot',
    platform: PLATFORM.HUBSPOT,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/hubspot.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/hubspot.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.HUBSPOT,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    isThirdPartyProvider: false,
    isManualAccountInput: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.crmMarketingAutomation,
    isInternalOnly: false
  },
  {
    label: 'Klaviyo',
    platform: PLATFORM.KLAVIYO,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/klaviyo.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/klaviyo.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.KLAVIYO,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    isThirdPartyProvider: false,
    isManualAccountInput: false,
    supportsServiceAccountAccess: false,
    supportsReconnect: false,
    category: PLATFORM_CATEGORIES.emailMarketing,
    isInternalOnly: false
  },
  {
    label: 'Spotify Ads',
    platform: PLATFORM.SPOTIFY_ADS,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/spotify_ads.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/spotify_ads.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.SPOTIFY_ADS,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    isThirdPartyProvider: false,
    isManualAccountInput: false,
    supportsServiceAccountAccess: false,
    supportsReconnect: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Reddit Ads',
    platform: PLATFORM.REDDIT_ADS,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/reddit_ads.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/reddit_ads.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.REDDIT_ADS,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    isThirdPartyProvider: false,
    isManualAccountInput: false,
    supportsServiceAccountAccess: false,
    supportsReconnect: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'RTB House',
    platform: PLATFORM.RTB_HOUSE,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/rtb_house.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/rtb_house.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.RTB_HOUSE,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    isThirdPartyProvider: false,
    isManualAccountInput: false,
    supportsServiceAccountAccess: false,
    supportsReconnect: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'WooCommerce',
    platform: PLATFORM.WOOCOMMERCE,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/woocommerce.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/woocommerce.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.WOOCOMMERCE,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntitiesLabel: 'accounts',
    externalEntityLabel: 'account',
    isThirdPartyProvider: false,
    isManualAccountInput: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.other,
    isInternalOnly: false
  },
  {
    label: 'CJ Affiliate',
    platform: PLATFORM.CJ_AFFILIATE,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/cj_affiliate.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/cj_affiliate.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.CJ_AFFILIATE,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.affiliateMarketing,
    isInternalOnly: false
  },
  {
    label: 'Tradedoubler',
    platform: PLATFORM.TRADEDOUBLER,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/tradedoubler.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/tradedoubler.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.TRADEDOUBLER,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.affiliateMarketing,
    isInternalOnly: false
  },
  {
    label: 'Campaign Manager 360',
    platform: PLATFORM.GOOGLE_CAMPAIGN_MANAGER360,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/cm_360_logo.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/cm_360_logo.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.GOOGLE_CAMPAIGN_MANAGER360,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Partnerize',
    platform: PLATFORM.PARTNERIZE,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/partnerize.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/partnerize.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.PARTNERIZE,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.affiliateMarketing,
    isInternalOnly: false
  },
  {
    label: 'StackAdapt',
    platform: PLATFORM.STACKADAPT,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/stackadapt.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/stackadapt.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.STACKADAPT,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Shopify (supermetrics)',
    platform: PLATFORM.SHOPIFY_SUPERMETRICS,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/shopify.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/shopify.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.SHOPIFY_SUPERMETRICS,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.other,
    isInternalOnly: false
  },
  {
    label: 'X Ads',
    platform: PLATFORM.X_ADS,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/x.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/x.svg`,
    oauthUrl: generateOauthLoginUrl(THIRD_PARTY_PROVIDER.SUPERMETRICS, PLATFORM.X_ADS, USE_PREPARING_PAGE_FOR_FIVETRAN),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Stripe',
    platform: PLATFORM.STRIPE,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/stripe.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/stripe.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.STRIPE,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.other,
    isInternalOnly: false
  },
  {
    label: 'Quantcast',
    platform: PLATFORM.QUANTCAST,
    thirdPartyProvider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/quantcast.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/quantcast.svg`,
    oauthUrl: generateOauthLoginUrl(
      THIRD_PARTY_PROVIDER.SUPERMETRICS,
      PLATFORM.QUANTCAST,
      USE_PREPARING_PAGE_FOR_FIVETRAN
    ),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    supportsServiceAccountAccess: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: true
  }
]

export const PLATFORMS: IPlatform[] = [
  {
    label: 'Google Analytics UA',
    platform: PLATFORM.ANALYTICS,
    iconUrl: `${ASSETS_BASE_URL}/google_analytics_ua.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'views',
    externalEntitiesLabel: 'views',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.GOOGLE_ANALYTICS_UA,
    category: PLATFORM_CATEGORIES.webAnalytics,
    isDeprecated: true,
    isInternalOnly: false
  },
  {
    label: 'Google Analytics 4',
    platform: PLATFORM.GA4,
    iconUrl: `${ASSETS_BASE_URL}/google_analytics_4.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'property',
    externalEntitiesLabel: 'properties',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.GOOGLE_ANALYTICS_4,
    category: PLATFORM_CATEGORIES.webAnalytics,
    isInternalOnly: false
  },
  {
    label: 'Google Ads',
    platform: PLATFORM.GOOGLE,
    iconUrl: `${ASSETS_BASE_URL}/google_ads.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: true,
    externalEntitiesLabel: 'accounts',
    uiUrlTemplate: 'https://ads.google.com/aw/accounts?account_id={accountId}',
    externalEntityLabel: 'account',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.GOOGLE_ADS,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Amazon Ads',
    platform: PLATFORM.AMAZON,
    iconUrl: `${ASSETS_BASE_URL}/amazon_ads.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/amazon_ads.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.AMAZON),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.AMAZON_ADS,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Criteo',
    platform: PLATFORM.CRITEO,
    iconUrl: `${ASSETS_BASE_URL}/criteo.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/criteo.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.CRITEO),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'DV360',
    platform: PLATFORM.DV360,
    iconUrl: `${ASSETS_BASE_URL}/dv360.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: true,
    externalEntitiesLabel: 'accounts',
    uiUrlTemplate: 'https://displayvideo.google.com/ng_nav/home_v3',
    externalEntityLabel: 'account',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.DV360,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Google Search Console',
    platform: PLATFORM.GOOGLE_SEARCH_CONSOLE,
    iconUrl: `${ASSETS_BASE_URL}/google_search_console.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'property',
    externalEntitiesLabel: 'properties',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.GOOGLE_SEARCH_CONSOLE,
    category: PLATFORM_CATEGORIES.seo,
    isInternalOnly: false
  },
  {
    label: 'Google Merchant Center',
    platform: PLATFORM.MERCHANT_CENTER,
    iconUrl: `${ASSETS_BASE_URL}/merchant_center.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.GOOGLE_MERCHANT_CENTER,
    category: PLATFORM_CATEGORIES.other,
    isInternalOnly: false
  },
  {
    label: 'Google Cloud Platform',
    platform: PLATFORM.GCP,
    iconUrl: `${ASSETS_BASE_URL}/gcp.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'project',
    externalEntitiesLabel: 'projects',
    supportsServiceAccountAccess: true,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    isConnectable: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.GOOGLE_CLOUD_PLATFORM,
    category: PLATFORM_CATEGORIES.dataTargets,
    isInternalOnly: false
  },
  {
    label: 'Microsoft Ads',
    platform: PLATFORM.MICROSOFT_ADVERTISING,
    iconUrl: `${ASSETS_BASE_URL}/microsoft_ads.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/microsoft_ads.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.MICROSOFT),
    isSupportedByChannelGrouping: true,
    externalEntitiesLabel: 'accounts',
    uiUrlTemplate: 'https://ads.microsoft.com/',
    externalEntityLabel: 'account',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.MICROSOFT_ADS,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'LinkedIn',
    platform: PLATFORM.LINKEDIN,
    iconUrl: `${ASSETS_BASE_URL}/linkedin.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/linkedin.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.LINKEDIN),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.LINKEDIN,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Meta Ads',
    platform: PLATFORM.FACEBOOK,
    iconUrl: `${ASSETS_BASE_URL}/meta_ads.png`,
    commonIconUrl: `${ASSETS_BASE_URL}/meta_ads.png`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.FACEBOOK),
    isSupportedByChannelGrouping: true,
    externalEntitiesLabel: 'accounts',
    uiUrlTemplate: 'https://www.facebook.com/adsmanager/manage/campaigns?act={accountId}',
    externalEntityLabel: 'account',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.FACEBOOK_ADS,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Snapchat',
    platform: PLATFORM.SNAPCHAT,
    iconUrl: `${ASSETS_BASE_URL}/snapchat.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/snapchat.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.SNAPCHAT),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.SNAPCHAT,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Tiktok',
    platform: PLATFORM.TIKTOK,
    iconUrl: `${ASSETS_BASE_URL}/tiktok.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/tiktok.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.TIKTOK),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.TIKTOK,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Pinterest',
    platform: PLATFORM.PINTEREST,
    iconUrl: `${ASSETS_BASE_URL}/pinterest.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/pinterest.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.PINTEREST),
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.PINTEREST,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: false
  },
  {
    label: 'Google Sheets',
    platform: PLATFORM.GOOGLE_SHEETS,
    iconUrl: `${ASSETS_BASE_URL}/google_sheets.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'spreadsheet',
    externalEntitiesLabel: 'spreadsheets',
    supportsServiceAccountAccess: false,
    isManualAccountInput: true,
    isThirdPartyProvider: false,
    intercomArticleId: INTERCOM_ARTICLE_IDS.GOOGLE_SHEETS,
    category: PLATFORM_CATEGORIES.other,
    isInternalOnly: false
  },
  {
    label: 'Google BigQuery',
    platform: PLATFORM.GOOGLE_BIGQUERY,
    iconUrl: `${ASSETS_BASE_URL}/google_bigquery.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
    oauthUrl: generateOauthLoginUrl(SupportedPlatformsEnum.GOOGLE),
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'table',
    externalEntitiesLabel: 'tables',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    isConnectable: false,
    isDataSource: false,
    category: PLATFORM_CATEGORIES.dataTargets,
    isInternalOnly: false
  },
  {
    label: 'Shopify',
    platform: PLATFORM.SHOPIFY,
    iconUrl: `${ASSETS_BASE_URL}/shopify.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/shopify.svg`,
    oauthUrl: '',
    isSupportedByChannelGrouping: false,
    externalEntityLabel: 'entity',
    externalEntitiesLabel: 'entities',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isThirdPartyProvider: false,
    isDataSource: false,
    category: PLATFORM_CATEGORIES.other,
    isInternalOnly: true,
    isDeprecated: true,
    isConnectable: false,
    disabledMessage: 'nativeShopifyDeprecated'
  },
  {
    label: 'ChatGPT Ads',
    platform: PLATFORM.CHATGPT_ADS,
    iconUrl: `${ASSETS_BASE_URL}/openai.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/openai.svg`,
    oauthUrl: '',
    isSupportedByChannelGrouping: true,
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isManualAccountInput: false,
    isApiKeyInput: true,
    isThirdPartyProvider: false,
    category: PLATFORM_CATEGORIES.paidMedia,
    isInternalOnly: true
  },
  ...THIRD_PARTY_PLATFORMS
]

export const UnknownPlatform: IPlatform = {
  label: 'Unknown',
  platform: PLATFORM_UNKNOWN as Platforms,
  iconUrl: `${ASSETS_BASE_URL}/unknown-icon.svg`,
  commonIconUrl: `${ASSETS_BASE_URL}/unknown-icon.svg`,
  oauthUrl: '',
  isSupportedByChannelGrouping: false,
  externalEntityLabel: 'entity',
  externalEntitiesLabel: 'entities',
  supportsServiceAccountAccess: false,
  isManualAccountInput: false,
  isThirdPartyProvider: false,
  isUnknown: true,
  category: PLATFORM_CATEGORIES.other,
  isInternalOnly: false
}

export const UnsupportedPlatform: (platform: string | undefined) => IPlatform = (platform) => ({
  ...UnknownPlatform,
  label: `Unsupported ${capitalize(platform ?? 'Platform')}`,
  platform: (platform ?? PLATFORM_UNKNOWN) as Platforms
})

export const THIRD_PARTY_PROVIDERS: IThirdPartyProvider[] = [
  {
    label: 'Fivetran',
    platform: THIRD_PARTY_PROVIDER.FIVETRAN,
    iconUrl: `${ASSETS_BASE_URL}/fivetran.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/fivetran.svg`,
    logoUrl: `${ASSETS_BASE_URL}/fivetran-logo.svg`,
    oauthUrl: generateOauthLoginUrl(THIRD_PARTY_PROVIDER.FIVETRAN),
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isThirdPartyProvider: true,
    filterCredentialsByClient: true,
    multipleTablesPerConnector: true,
    prepare: async (clientId: number, platform: ThirdPartyPlatforms, credentialId: string | null) =>
      await prepareThirdPartyDataSource({
        clientId,
        provider: THIRD_PARTY_PROVIDER.FIVETRAN,
        platform,
        credentialId
      }),
    reconnect: async (clientId, platform, credentialId, thirdPartyId, reconnectAccountId) =>
      await reconnectThirdPartyDataSource(
        clientId,
        THIRD_PARTY_PROVIDER.FIVETRAN,
        platform,
        credentialId,
        thirdPartyId,
        reconnectAccountId
      ),
    hasPlatformSpecificCredentials: false,
    shouldReconnect: (credentialId, thirdPartyId, reconnectAccountId) =>
      credentialId != null && thirdPartyId != null && reconnectAccountId != null,
    isInternalOnly: true
  },
  {
    label: 'Supermetrics',
    platform: THIRD_PARTY_PROVIDER.SUPERMETRICS,
    iconUrl: `${ASSETS_BASE_URL}/supermetrics.svg`,
    commonIconUrl: `${ASSETS_BASE_URL}/supermetrics.svg`,
    logoUrl: `${ASSETS_BASE_URL}/supermetrics_logo.svg`,
    oauthUrl: generateOauthLoginUrl(THIRD_PARTY_PROVIDER.SUPERMETRICS),
    externalEntityLabel: 'account',
    externalEntitiesLabel: 'accounts',
    supportsServiceAccountAccess: false,
    isThirdPartyProvider: true,
    prepare: async (clientId: number, platform: ThirdPartyPlatforms, credentialId: string | null) =>
      await prepareThirdPartyDataSource({
        clientId,
        provider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
        platform,
        credentialId
      }).then((response) => {
        if (response.error != null) {
          return {
            error: response.error,
            errorCode:
              response.error.includes(SUPERMETRICS_ERROR_CODES.ACCESS_TOKEN_INVALID) ||
              response.statusCode === HttpStatusCodes.NOT_FOUND
                ? ErrorCodesEnum.SYSTEM_SETUP_INCOMPLETE
                : ErrorCodesEnum.UNKNOWN
          }
        }
        return response
      }),
    reconnect: async (clientId, platform, credentialId) =>
      await prepareThirdPartyDataSource({
        clientId,
        provider: THIRD_PARTY_PROVIDER.SUPERMETRICS,
        platform,
        customQueryParams: {
          login_id: credentialId
        }
      }),
    hasPlatformSpecificCredentials: true,
    shouldReconnect: (credentialId) => credentialId != null,
    isInternalOnly: false
  }
]

const reEmptyString = /^\s*$/

const zodConfigCommon = {
  clientId: zod
    .string()
    .nonempty('Empty clientId')
    .transform((queryClientId, ctx) => {
      const alvieClientId = parseInt(queryClientId, 10)
      if (isNaN(alvieClientId)) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Invalid clientId'
        })
        return zod.NEVER
      }
      return alvieClientId
    }),
  provider: zod
    .string()
    .nonempty('Empty third party provider')
    .transform((queryProvider) => {
      const providerConfig = THIRD_PARTY_PROVIDERS.find(({ platform }) => platform === queryProvider)
      return {
        provider: providerConfig?.platform,
        providerConfig
      }
    })
    .refine(({ provider, providerConfig }) => provider != null && providerConfig != null, {
      message: 'Invalid third party provider'
    })
    // Only Fivetran is supported by these third-party query schemas (same gate as the former
    // supportedPlatformConfigs[FIVETRAN] lookup, without importing oauth and creating a cycle).
    .refine(({ provider }) => provider === THIRD_PARTY_PROVIDER.FIVETRAN, {
      message: 'Unsupported third party provider'
    })
    .transform((provider) => ({
      provider: provider.provider as ThirdPartyProviders,
      providerConfig: provider.providerConfig as IThirdPartyProvider
    })),
  platform: zod
    .string()
    .nonempty('Empty platform')
    .transform((queryPlatform) => {
      const platformConfig = THIRD_PARTY_PLATFORMS.find(({ platform }) => platform === queryPlatform)
      return {
        platform: platformConfig?.platform,
        platformConfig
      }
    })
    .refine(({ platform, platformConfig }) => platform != null && platformConfig != null, {
      message: 'Invalid platform'
    })
    .transform(({ platform, platformConfig }) => ({
      platform: platform as FivetranSupportedPlatform,
      platformConfig: platformConfig as IPlatform
    }))
}

const zodConfigPrepare = {
  ...zodConfigCommon,
  credentialId: zod.string().optional()
}

const zodConfigReconnect = {
  ...zodConfigCommon,
  credentialId: zod.string().nonempty('Empty credentialId'),
  thirdPartyId: zod.string().nonempty('Empty thirdPartyId'),
  reconnectAccountId: zod
    .string()
    .nonempty('Empty reconnectAccountId')
    .refine((queryReconnectAccountId) => !reEmptyString.test(queryReconnectAccountId), 'Invalid reconnectAccountId')
}

const zodConfigCallback = {
  ...zodConfigCommon,
  credentialId: zod.string().nonempty('Empty credentialId'),
  id: zod.string().nonempty('Empty thirdPartyId'),
  reconnectAccountId: zod
    .string()
    .optional()
    .refine(
      (queryReconnectAccountId) => queryReconnectAccountId == null || !reEmptyString.test(queryReconnectAccountId),
      'Invalid reconnectAccountId'
    )
}

const zodConfigCleanup = {
  ...zodConfigCommon,
  credentialId: zod.string().optional(),
  thirdPartyId: zod.string().nonempty('Empty thirdPartyId')
}

export const QuerySchemaThirdPartyPrepare = zod.object(zodConfigPrepare).superRefine((query, ctx) => {
  validateThirdPartyId(query.provider.provider, query.credentialId, 'credentialId', ctx)
})
export const QuerySchemaThirdPartyReconnect = zod.object(zodConfigReconnect).superRefine((query, ctx) => {
  validateThirdPartyId(query.provider.provider, query.credentialId, 'credentialId', ctx)
  validateThirdPartyId(query.provider.provider, query.thirdPartyId, 'thirdPartyId', ctx)
})
export const QuerySchemaThirdPartyCallback = zod
  .object(zodConfigCallback)
  .transform(({ id, ...rest }) => ({ thirdPartyId: id, ...rest }))
  .superRefine((query, ctx) => {
    validateThirdPartyId(query.provider.provider, query.credentialId, 'credentialId', ctx)
    validateThirdPartyId(query.provider.provider, query.thirdPartyId, 'thirdPartyId', ctx)
  })
export const QuerySchemaThirdPartyCleanup = zod.object(zodConfigCleanup).superRefine((query, ctx) => {
  validateThirdPartyId(query.provider.provider, query.credentialId, 'credentialId', ctx)
  validateThirdPartyId(query.provider.provider, query.thirdPartyId, 'thirdPartyId', ctx)
})

export const PLATFORMS_CATEGORIES_ORDER: PlatformCategory[] = [
  'Web Analytics',
  'Paid Media',
  'SEO',
  'Data Targets',
  'Other',
  'CRM / Marketing Automation',
  'Affiliate Marketing',
  'Email Marketing'
]

export const DEPRECATED_DATA_SOURCE_PLATFORMS: Platforms[] = [PLATFORM.ANALYTICS]
